import React from "react";
import { Link } from "react-router-dom";
import { Clock, CheckCircle2, XCircle, AlertCircle, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const META = {
  Pending: {
    color: "bg-stone-100 border-stone-300 text-stone-900",
    accent: "text-stone-700",
    icon: AlertCircle,
    title: "Complete your profile to unlock payouts",
    desc: "Add your KYC and bank details — it takes 2 minutes. You can browse products before that.",
    cta: "Complete profile",
  },
  Submitted: {
    color: "bg-amber-50 border-amber-300 text-amber-900",
    accent: "text-amber-800",
    icon: Clock,
    title: "Profile under review by Operations",
    desc: "Your KYC has been submitted. NEFT withdrawals unlock as soon as it's verified (usually within 24h).",
    cta: "View status",
  },
  Approved: null, // No banner when fully approved — keeps dashboard clean.
  Rejected: {
    color: "bg-red-50 border-red-300 text-red-900",
    accent: "text-red-700",
    icon: XCircle,
    title: "Profile rejected — please update",
    desc: "Operations team flagged issues. Fix them and resubmit to unlock withdrawals.",
    cta: "Fix profile",
  },
};

// Used by user-facing pages to nudge KYC completion.
// Hides itself when the partner is fully Approved or when there is no user.
export default function ProfileStatusBanner() {
  const { user } = useAuth();
  if (!user || user.role !== "partner") return null;
  const status = user.profile_status || "Pending";
  const meta = META[status];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <div
      role="status"
      data-testid={`dashboard-profile-banner-${status.toLowerCase()}`}
      className={`rounded-2xl border-2 ${meta.color} p-5 flex flex-wrap gap-4 items-start justify-between`}
    >
      <div className="flex gap-3 min-w-0">
        <Icon className={`w-6 h-6 mt-0.5 shrink-0 ${meta.accent}`} />
        <div className="min-w-0">
          <div className="font-display font-black text-lg leading-tight" data-testid="dashboard-profile-banner-title">{meta.title}</div>
          <p className="text-sm mt-1 opacity-90 max-w-2xl">{meta.desc}</p>
          {status === "Rejected" && user.profile_rejection_reason && (
            <p className="text-xs mt-2 font-mono bg-white/60 rounded px-3 py-1.5" data-testid="dashboard-profile-banner-reason">
              Operations note: {user.profile_rejection_reason}
            </p>
          )}
        </div>
      </div>
      <Link
        to="/app/profile"
        data-testid="dashboard-profile-banner-cta"
        className={`inline-flex items-center gap-1.5 rounded-full bg-white border ${
          status === "Rejected" ? "border-red-300 text-red-900 hover:bg-red-50" :
          status === "Submitted" ? "border-amber-300 text-amber-900 hover:bg-amber-100" :
          "border-stone-300 text-stone-900 hover:bg-stone-50"
        } px-5 py-2 text-sm font-bold shrink-0`}
      >
        {meta.cta} <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

export const PROFILE_STATUS_META = META;
